import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ChevronDown, BadgeCheck, Sparkles, Clock, HeartHandshake, ArrowRight, MapPin } from "lucide-react";
import Reveal, { maskLine } from "../components/Reveal";
import Marquee from "../components/Marquee";
import EnquirySection from "../components/EnquirySection";
import { BUSINESS, SERVICES, GALLERY, PILLARS, waLink, HERO_IMAGE } from "../lib/constants";
import { usePageTitle } from "../lib/seo";

const ICONS = { badge: BadgeCheck, sparkles: Sparkles, clock: Clock, heart: HeartHandshake };

export default function Home() {
  usePageTitle("");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMAGE} alt="Wedding tent arranged by Jaiswal Tent House" className="h-full w-full scale-110 object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0908] via-[#0A0908]/75 to-[#0A0908]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-transparent to-[#0A0908]/60" />

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-5 pt-28 sm:px-8">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
          >
            Tent House &amp; Event Decoration · Bilaspur, Chhattisgarh
          </motion.p>

          <h1 className="mt-6 font-display text-5xl font-bold leading-[0.98] sm:text-6xl lg:text-[5.5rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-cream" variants={maskLine} custom={0} initial="hidden" animate="show">
                Jaiswal
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-gold-grad" variants={maskLine} custom={1} initial="hidden" animate="show">
                Tent House
              </motion.span>
            </span>
          </h1>

          <motion.p
            className="mt-5 font-display text-2xl italic text-amber-200/95 sm:text-3xl"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
          >
            {BUSINESS.tagline}
          </motion.p>

          <motion.p
            className="mt-5 max-w-xl text-sm leading-relaxed text-stone-300 sm:text-base"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            {BUSINESS.description}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
          >
            <a href={BUSINESS.phoneHref} className="btn-gold" data-testid="hero-call-btn">
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <a
              href={waLink("Hello Jaiswal Tent House! I would like to enquire about your services.")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp Us
            </a>
            <Link to="/services" className="btn-outline-gold" data-testid="hero-services-btn">
              Explore Our Services <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            className="mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <MapPin className="h-3.5 w-3.5 text-gold" /> Serving Bilaspur &amp; nearby areas
          </motion.div>
        </motion.div>

        <div className="absolute bottom-24 left-1/2 hidden -translate-x-1/2 md:bottom-8 md:block" style={{ opacity: fade }}>
          <ChevronDown className="animate-scroll-cue h-6 w-6 text-gold/70" />
        </div>
      </section>

      <Marquee
        items={["Weddings", "Receptions", "Birthdays", "Haldi", "Engagement", "Pandal Setup", "Stage Decor", "Lighting & Flowers"]}
      />

      {/* ===== FEATURED SERVICES ===== */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 md:py-28">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow">What We Do</p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-amber-100 sm:text-4xl lg:text-5xl">
                Celebrations, <span className="text-gold-grad italic">beautifully arranged</span>
              </h2>
            </div>
            <Link to="/services" data-testid="home-view-all-services-btn" className="btn-outline-gold self-start !py-2.5 sm:self-auto">
              All 8 Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.slice(0, 4).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08} className="h-full">
              <Link
                to="/services"
                data-testid={`home-service-card-${s.slug}`}
                className="card-royal group relative block h-full overflow-hidden transition-all duration-500 hover:-translate-y-1.5"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14120E] via-transparent to-transparent" />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl text-amber-100">{s.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-stone-400">{s.short}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all group-hover:gap-3">
                    Details <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="border-y border-gold/15 bg-[#FAF6EE] py-20 text-[#14120E] md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow !text-gold-deep">Why Jaiswal Tent House</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              Celebrations Planned with <span className="italic text-gold-deep">Care.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.title} delay={i * 0.08} className="h-full">
                  <div className="h-full rounded-2xl border border-[#AA771C]/25 bg-white/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(170,119,28,0.18)]">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#BF953F] to-[#AA771C] text-[#FAF6EE]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-semibold">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-stone-600">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== GALLERY SHOWCASE ===== */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 md:py-28">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow">Our Work</p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-amber-100 sm:text-4xl lg:text-5xl">
                Real setups, <span className="text-gold-grad italic">real celebrations</span>
              </h2>
            </div>
            <Link to="/gallery" data-testid="home-view-gallery-btn" className="btn-outline-gold self-start !py-2.5 sm:self-auto">
              View Full Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3">
          {GALLERY.slice(0, 6).map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.08}>
              <div className="group relative overflow-hidden rounded-xl border border-gold/15">
                <img
                  src={g.src}
                  alt={g.title}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-110 md:aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <p className="absolute bottom-3 left-4 font-display text-lg text-cream opacity-0 transition-all duration-500 group-hover:opacity-100">
                  {g.title}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== ENQUIRY ===== */}
      <EnquirySection />

      {/* ===== MAP ===== */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <div className="card-royal overflow-hidden">
            <div className="flex flex-col justify-between gap-3 p-6 sm:flex-row sm:items-center sm:p-8">
              <div>
                <p className="eyebrow">Find Us</p>
                <h3 className="mt-2 font-display text-2xl text-amber-100">Based in Bilaspur, Chhattisgarh</h3>
              </div>
              <a
                href={BUSINESS.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-maps-directions-btn"
                className="btn-outline-gold !py-2.5"
              >
                <MapPin className="h-4 w-4" /> Open in Google Maps
              </a>
            </div>
            <iframe
              title="Jaiswal Tent House location — Bilaspur, Chhattisgarh"
              src={BUSINESS.mapsEmbed}
              className="h-[320px] w-full border-0 grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
